import type { AiOperationsRun, AiRunStatus } from "@adpilot/shared";
import { type ReportingTimeZone, reportingDateInTimeZone } from "@/lib/reporting-time-zone";

/** Action outcome counts of one AI run cycle, in the order of the status bar segments. */
export interface AiRunBreakdown {
  startedAt: string;
  executed: number;
  toReview: number;
  failed: number;
  noChange: number;
}

export interface AiActivitySummary {
  lastRun: AiRunBreakdown | null;
  appliedToday: number;
  /** Share of today's applied actions that ran without review (0–1); null when none applied. */
  autoShareToday: number | null;
  failedRuns: AiOperationsRun[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
// A scheduled sweep starts one run per campaign over a short window; there is no
// cycle id on the API, so cron runs this close to the latest one count as its cycle.
const CRON_CYCLE_WINDOW_MS = 2 * 60 * 60 * 1000;

function byStartedDesc(a: AiOperationsRun, b: AiOperationsRun): number {
  return Date.parse(b.startedAt) - Date.parse(a.startedAt);
}

/** Runs from the last 24 hours whose Amazon execution failed — the "Issues" count. */
export function selectFailedRuns(runs: readonly AiOperationsRun[], now: Date): AiOperationsRun[] {
  const since = now.getTime() - DAY_MS;
  return runs
    .filter(
      (run) => Date.parse(run.startedAt) >= since && (run.status === "failed" || run.failed > 0),
    )
    .sort(byStartedDesc);
}

function lastRunCycle(runs: readonly AiOperationsRun[]): AiOperationsRun[] {
  const [latest] = [...runs].sort(byStartedDesc);
  if (!latest) return [];
  if (latest.batchId) return runs.filter((run) => run.batchId === latest.batchId);
  if (latest.triggerType !== "cron") return [latest];
  const latestAt = Date.parse(latest.startedAt);
  return runs.filter(
    (run) =>
      run.triggerType === "cron" &&
      run.batchId === null &&
      latestAt - Date.parse(run.startedAt) <= CRON_CYCLE_WINDOW_MS,
  );
}

export function summarizeAiActivity(
  runs: readonly AiOperationsRun[],
  now: Date,
  timeZone: ReportingTimeZone,
): AiActivitySummary {
  const cycle = lastRunCycle(runs);
  const earliest = [...cycle].sort(byStartedDesc).at(-1);
  const lastRun: AiRunBreakdown | null = earliest
    ? {
        startedAt: earliest.startedAt,
        executed: cycle.reduce((sum, run) => sum + run.executedActions, 0),
        toReview: cycle.reduce((sum, run) => sum + run.pendingDecisions, 0),
        failed: cycle.reduce((sum, run) => sum + run.failed, 0),
        noChange: cycle.reduce((sum, run) => sum + run.skipped, 0),
      }
    : null;

  const today = reportingDateInTimeZone(now, timeZone);
  const todayRuns = runs.filter(
    (run) => reportingDateInTimeZone(new Date(run.startedAt), timeZone) === today,
  );
  const appliedToday = todayRuns.reduce((sum, run) => sum + run.executedActions, 0);
  const autoToday = todayRuns.reduce((sum, run) => sum + run.autoExecuted, 0);

  return {
    lastRun,
    appliedToday,
    autoShareToday: appliedToday > 0 ? Math.min(1, autoToday / appliedToday) : null,
    failedRuns: selectFailedRuns(runs, now),
  };
}

/** Most recent run statuses per campaign, oldest → newest, for the "AI runs" mini bars. */
export function recentRunStatusesByCampaign(
  runs: readonly AiOperationsRun[],
  limit = 5,
): Map<string, AiRunStatus[]> {
  const result = new Map<string, AiRunStatus[]>();
  for (const run of [...runs].sort(byStartedDesc)) {
    if (!run.campaignId) continue;
    const statuses = result.get(run.campaignId) ?? [];
    if (statuses.length < limit) statuses.unshift(run.status);
    result.set(run.campaignId, statuses);
  }
  return result;
}
